import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { addCartLines, cartQueryKey, cartUrl, createCart, fetchCart, fetchCarts, removeCartLine, updateCartLine, type Cart } from "../api/carts";
import { ApiError } from "../lib/api";
import { dataContextGuard } from "../lib/dataContext";
import QtyStepper from "./QtyStepper";
import { useActiveCartId } from "../lib/activeCart";
import { isDemoMode } from "../lib/env";

const currentCartQueryKey = (activeCartId: string | null) => ["carts", "list", "current-open", activeCartId] as const;

/** The chosen open cart, else the most recently edited one (#547). */
async function findCurrentCart(activeCartId: string | null) {
  const mine = await fetchCarts("mine", null, "open");
  return mine.items.find((cart) => cart.cart_id === activeCartId) ?? mine.items[0] ?? null;
}

async function fetchCurrentCart(activeCartId: string | null): Promise<Cart | null> {
  const current = await findCurrentCart(activeCartId);
  return current ? fetchCart(current.cart_id) : null;
}

/** Der aktive offene Warenkorb; auch die Kopfzeile zeigt seine Anzahl (#630). */
export function useCurrentCart() {
  const [activeCartId] = useActiveCartId();
  return useQuery({
    queryKey: currentCartQueryKey(activeCartId),
    queryFn: () => fetchCurrentCart(activeCartId),
    enabled: !isDemoMode(),
    staleTime: 30_000,
  });
}

/** Adds an article to the active open cart, or to a new one (#547). */
export async function addToCurrentCart(articleGroupKey: string, activeCartId: string | null, qty = 1): Promise<Cart> {
  const checkContext = dataContextGuard();
  let current = await findCurrentCart(activeCartId);
  checkContext();
  current ??= await createCart();
  checkContext();
  return addCartLines(current, [{ article_group_key: articleGroupKey, qty }]);
}

export default function AddToCartButton({
  articleGroupKey,
  articleLabel,
  compact = false,
}: {
  articleGroupKey: string;
  articleLabel?: string;
  compact?: boolean;
}) {
  const queryClient = useQueryClient();
  const [activeCartId, setActiveCartId] = useActiveCartId();
  // One shared query serves all visible article rows; it also survives a reload.
  const currentCartQuery = useCurrentCart();
  const currentCart = currentCartQuery.data;
  const line = currentCart?.lines.find(item => item.article_group_key === articleGroupKey);
  const mutation = useMutation({
    onMutate: () => ({
      activate: (id: string) => setActiveCartId(id, activeCartId),
      checkContext: dataContextGuard(),
    }),
    // "add" puts one more in (a second add raises the quantity); "less" takes
    // one out and removes the line at the last one.
    mutationFn: async (action: "add" | "less" | { set: number }) => {
      if (action === "add") return addToCurrentCart(articleGroupKey, activeCartId);
      if (!currentCart) throw new Error("Kein offener Warenkorb vorhanden.");
      // Read the latest revision first, so another cart edit is not overwritten.
      const fresh = await fetchCart(currentCart.cart_id);
      const freshLine = fresh.lines.find(item => item.article_group_key === articleGroupKey);
      if (!freshLine) return fresh;
      if (typeof action === "object") return updateCartLine(fresh, freshLine.line_id, { qty: action.set });
      const qty = Number(freshLine.qty);
      return qty > 1 ? updateCartLine(fresh, freshLine.line_id, { qty: qty - 1 }) : removeCartLine(fresh, freshLine.line_id);
    },
    onSuccess: (cart, _action, started) => {
      started?.checkContext();
      queryClient.setQueryData(cartQueryKey(cart.cart_id), cart);
      // A cart created on the first add becomes the active one.
      started?.activate(cart.cart_id);
      queryClient.setQueryData(currentCartQueryKey(cart.cart_id), cart);
      void queryClient.invalidateQueries({ queryKey: ["carts", "list"] });
    },
  });
  if (isDemoMode()) return null;

  if (line && currentCart) {
    const qty = Number(line.qty);
    const name = articleLabel ?? "Artikel";
    return (
      <span className="inline-flex max-w-full flex-wrap items-center justify-end gap-1.5" role="group" aria-label={`${name} im Warenkorb „${currentCart.name}“`}>
        {currentCart.can_edit ? (
          <QtyStepper
            value={line.qty}
            label={`Menge ${name}`}
            size={compact ? "sm" : "md"}
            disabled={mutation.isPending}
            onLess={() => mutation.mutate("less")}
            onMore={() => mutation.mutate("add")}
            onSet={(next) => mutation.mutate({ set: next })}
            lessLabel={qty > 1 ? `Einen ${name} weniger` : `${name} aus dem Warenkorb nehmen`}
          />
        ) : null}
        {currentCart.can_edit && line.uom ? (
          // Same unit label as in the cart.
          <span className="text-xs text-slate-500">{line.uom.trim().toUpperCase() === "ST" ? "Stk" : line.uom}</span>
        ) : null}
        {currentCart.can_edit ? null : (
          <span className="text-sm tabular-nums text-slate-700">{qty.toLocaleString("de-AT")} im Warenkorb</span>
        )}
        <Link
          to={cartUrl(currentCart.cart_id)}
          title={`Warenkorb „${currentCart.name}“ öffnen`}
          aria-label={`Warenkorb „${currentCart.name}“ öffnen`}
          className={`inline-flex items-center gap-1 rounded-md px-1.5 text-slate-500 hover:bg-slate-100 hover:text-emerald-800 ${compact ? "h-8" : "h-9 text-sm"}`}
        >
          <svg aria-hidden="true" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="20" r="1.4" /><circle cx="18" cy="20" r="1.4" /><path d="M2.5 3.5h2.6l2.4 11.2a1.6 1.6 0 0 0 1.6 1.3h8.6a1.6 1.6 0 0 0 1.6-1.2l1.7-7.3H6.1" /></svg>
          {compact ? null : <span>{currentCart.name}</span>}
        </Link>
        {mutation.isError ? <span role="alert" className="basis-full text-right text-xs text-rose-700">{mutation.error instanceof ApiError ? mutation.error.message : "Die Menge konnte nicht geändert werden."}</span> : null}
      </span>
    );
  }
  return (
    <span className="inline-flex flex-col items-start gap-1">
      <button
        type="button"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          mutation.mutate("add");
        }}
        disabled={mutation.isPending || currentCartQuery.isPending}
        className={
          compact
            ? "h-8 whitespace-nowrap rounded-md border border-slate-300 bg-white px-2.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            : "h-9 whitespace-nowrap rounded-md bg-emerald-700 px-3 text-sm font-medium text-white hover:bg-emerald-800 disabled:opacity-50"
        }
      >
        {mutation.isPending ? "Lege in den Warenkorb …" : "In den Warenkorb"}
      </button>
      {currentCartQuery.isError ? <span role="alert" className="text-xs text-rose-700">Warenkorb nicht erreichbar.</span> : null}
      {mutation.isError ? (
        <span role="alert" className="text-xs text-rose-700">
          {mutation.error instanceof ApiError ? mutation.error.message : "Konnte nicht in den Warenkorb gelegt werden."}
        </span>
      ) : null}
    </span>
  );
}
